import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';
import { Text } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { SideBarItem } from './SideBarItem';
import { getUserSubject, User, UserRole } from '../../../store/User';
import { useAuth } from '../../../Context/AuthContext';

export const SideNavBar = () => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [user, setUser] = useState<User>();
  const location = useLocation();
  const { signOut, authState } = useAuth();

  const name = location.pathname.replace('/', '');
  const changeExpanded = (): void => setExpanded(!expanded);

  const handleSignOut = async () => {
    await signOut();
  };

  useEffect(() => {
    const userSub = getUserSubject().subscribe((u) => setUser(u));

    return () => {
      if (userSub) userSub.unsubscribe();
    };
  }, []);

  const isAdminEngineer = [UserRole.ADMIN, UserRole.ENGINEER].includes(user?.role as UserRole);
  const isAdminEngineerData = isAdminEngineer || user?.role === UserRole.DATA;
  const isAdminEngineerDataAnalyst = isAdminEngineerData || user?.role === UserRole.ANALYST;
  const isAdminEngineerAnalyst = isAdminEngineer || user?.role === UserRole.ANALYST;
  const isAssetRevenueAttributionActive = name.startsWith('asset-revenue-attribution');
  const isCommissionsSection =
    name.startsWith('link') ||
    name.startsWith('runs') ||
    name.startsWith('payment') ||
    isAssetRevenueAttributionActive ||
    name.startsWith('correction') ||
    name.startsWith('attribution/direct-seller') ||
    name.startsWith('adjustment');
  return (
    <>
      {authState?.isAuthenticated && user?.role !== UserRole.INACTIVE ? (
        <>
          <div
            data-testid="side-nav-bar-container"
            data-cy="sidebar"
            className="sidebar-wrapper z-10 max-h-0 text-white">
            <div
              className={classNames('sidebar min-h-screen border-r hover:shadow-lg min-w bg-grey-1', {
                'w-[3.35rem]': !expanded,
                'w-48': expanded
              })}
              data-testid="sidebar">
              <div className="flex h-screen flex-col justify-between pb-6">
                <div>
                  <div className="border-b border-gray-200 w-full overflow-hidden">
                    <Link
                      className="flex items-center ml-auto min-w-[10rem] with-rectangle after:bg-sky-50 after:w-3"
                      title="Home"
                      to={'/'}>
                      <img
                        src={'/UPSTACK-Logo.png'}
                        className="h-14 mr-3 ml-[6px] mt-[6px] mb-[10px]"
                        alt="UPSTACK Logo"
                      />
                    </Link>
                  </div>
                  <ul className="pt-4 space-y-2 tracking-wide">
                    <SideBarItem
                      navLinkProps={{ to: '' }}
                      iconProps={{ type: 'homeIcon' }}
                      textProps={{ children: 'Dashboard' }}
                      active={name === ''}
                      dataCy="dashboard-sidebar"
                      key="dashboard-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/customer' }}
                      iconProps={{ type: 'customersBuilding' }}
                      textProps={{ children: 'Customers' }}
                      active={name === 'customer'}
                      dataCy="customers-sidebar"
                      key="customers-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/asset' }}
                      iconProps={{ type: 'assetsDoc' }}
                      textProps={{ children: 'Assets' }}
                      active={name === 'asset'}
                      dataCy="asset-sidebar"
                      key="asset-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/provisional-asset' }}
                      iconProps={{ type: 'provisionalAssetsDoc' }}
                      textProps={{ children: 'Provisional' }}
                      active={name === 'provisional-asset'}
                      dataCy="provisional-asset-sidebar"
                      key="provisional-asset-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/rpm-account' }}
                      iconProps={{ type: 'rpmIconSidebar' }}
                      textProps={{ children: 'RPM Accounts' }}
                      active={name === 'rpm-account'}
                      dataCy="rpmAccounts-sidebar"
                      key="rpmAccounts-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/ticket' }}
                      iconProps={{ type: 'ticketsIcon' }}
                      textProps={{ children: 'Tickets' }}
                      active={name === 'ticket'}
                      dataCy="tickets-sidebar"
                      key="tickets-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/history' }}
                      iconProps={{ type: 'historyIcon' }}
                      textProps={{ children: 'History' }}
                      active={name === 'history'}
                      dataCy="history-sidebar"
                      key="history-sidebar"
                    />
                    <SideBarItem
                      navLinkProps={{ to: '/runs' }}
                      iconProps={{ type: 'paymentIcon' }}
                      textProps={{ children: 'Commissions' }}
                      active={isCommissionsSection}
                      dataCy="commissions-sidebar"
                      key="commissions-sidebar"
                    />
                    {isAdminEngineerData && (
                      <SideBarItem
                        navLinkProps={{ to: '/data/supplier-map' }}
                        iconProps={{ type: 'dataIcon' }}
                        textProps={{ children: 'Data' }}
                        active={name.startsWith('data') || name.startsWith('bulk-merge')}
                        dataCy="data-sidebar"
                        key="data-sidebar"
                      />
                    )}
                    {isAdminEngineer && (
                      <SideBarItem
                        navLinkProps={{ to: '/admin/users' }}
                        iconProps={{ type: 'adminIcon' }}
                        textProps={{ children: 'Admin' }}
                        active={name.startsWith('admin')}
                        dataCy="admin-sidebar"
                        key="admin-sidebar"
                      />
                    )}
                  </ul>
                </div>
                <div className="-mb-5 border-t w-full flex justify-center">
                  <button
                    className="flex items-center space-x-4 m-2 p-2 pl-1 text-gray-600 hover:bg-greenActive w-full overflow-hidden whitespace-nowrap rounded"
                    onClick={handleSignOut}>
                    <Icon type="logoutIcon" />
                    <Text
                      dataTestId="logout-button"
                      color="black"
                      size="base"
                      weight="normal">
                      Log Out
                    </Text>
                  </button>
                </div>
              </div>
              <button
                className={classNames('expand-button arrow-sidebar z-10', { 'reverse-button': expanded })}
                onClick={changeExpanded}
              />
            </div>
            <button
              aria-label="expand-button-external"
              className={classNames('expand-button arrow-sidebar', { 'reverse-button': expanded })}
              onClick={changeExpanded}
            />
          </div>
          {((isAdminEngineer && name.startsWith('admin')) ||
            (isAdminEngineerData && (name.startsWith('data') || name.startsWith('bulk-merge'))) ||
            isCommissionsSection) && (
            <div
              data-testid="extra-nav-bar-container"
              className={classNames('sidebar-wrapper z-9 max-h-0 text-white', {
                'left-48': expanded,
                'left-[3.35rem]': !expanded
              })}>
              <div className="w-48 sidebar min-h-screen border-r hover:shadow-lg min-w bg-white">
                <div className="flex h-screen flex-col justify-between">
                  <div>
                    <div className="space-y-1 tracking-wide">
                      {isCommissionsSection && (
                        <>
                          <SideBarItem
                            className="h-12"
                            navLinkProps={{ to: '/runs' }}
                            textProps={{ children: 'Runs', className: 'pl-1' }}
                            active={name === 'runs'}
                            dataCy="runs-sidebar"
                            key="runs-sidebar"
                          />
                          <SideBarItem
                            className="h-12"
                            navLinkProps={{ to: '/link' }}
                            textProps={{ children: 'Links', className: 'pl-1' }}
                            active={name.startsWith('link')}
                            dataCy="links-sidebar"
                            key="links-sidebar"
                          />
                          <SideBarItem
                            className="h-12"
                            navLinkProps={{ to: '/correction' }}
                            textProps={{ children: 'Corrections', className: 'pl-1' }}
                            active={name.startsWith('correction')}
                            dataCy="corrections-sidebar"
                            key="corrections-sidebar"
                          />
                          <SideBarItem
                            className="h-12"
                            navLinkProps={{ to: '/adjustment' }}
                            textProps={{ children: 'Adjustments', className: 'pl-1' }}
                            active={name.startsWith('adjustment')}
                            dataCy="adjustment-sidebar"
                            key="adjustment-sidebar"
                          />
                          {isAdminEngineerDataAnalyst && (
                            <SideBarItem
                              className="h-12"
                              navLinkProps={{ to: '/payment' }}
                              textProps={{ children: 'Payments', className: 'pl-1' }}
                              active={name.startsWith('payment')}
                              dataCy="payments-sidebar"
                              key="payments-sidebar"
                            />
                          )}
                          {isAdminEngineerAnalyst && (
                            <SideBarItem
                              className="min-h-12"
                              navLinkProps={{ to: '/asset-revenue-attribution' }}
                              wrap
                              textProps={{
                                children: 'Asset Revenue Attribution',
                                className: 'pl-1 leading-tight'
                              }}
                              active={isAssetRevenueAttributionActive}
                              dataCy="asset-revenue-attribution-sidebar"
                              key="asset-revenue-attribution-sidebar"
                            />
                          )}
                          <SideBarItem
                            className="h-12"
                            navLinkProps={{ to: '/attribution/direct-seller' }}
                            textProps={{ children: 'Direct Sellers', className: 'pl-1' }}
                            active={name.startsWith('attribution/direct-seller')}
                            dataCy="direct-seller-sidebar"
                            key="direct-seller-sidebar"
                          />
                        </>
                      )}
                      {isAdminEngineer && (
                        <>
                          {name.startsWith('admin') && (
                            <>
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/admin/users' }}
                                textProps={{ children: 'Users', className: 'pl-1' }}
                                active={name === 'admin/users'}
                                dataCy="admin-users-sidebar"
                                key="admin-users-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/admin/fields' }}
                                textProps={{ children: 'Fields', className: 'pl-1' }}
                                active={name === 'admin/fields'}
                                dataCy="admin-fields-sidebar"
                                key="admin-fields-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/admin/log' }}
                                textProps={{ children: 'Log', className: 'pl-1' }}
                                active={name === 'admin/log'}
                                dataCy="admin-log-sidebar"
                                key="admin-log-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/admin/run-total-audits' }}
                                textProps={{ children: 'Run Totals Audit', className: 'pl-1' }}
                                active={name.includes('admin/run-total-audits')}
                                dataCy="admin-audit-sidebar"
                                key="admin-audit-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/admin/instance' }}
                                textProps={{ children: 'Instance', className: 'pl-1' }}
                                active={name === 'admin/instance'}
                                dataCy="admin-instance-sidebar"
                                key="admin-instance-sidebar"
                              />
                            </>
                          )}
                        </>
                      )}
                      {isAdminEngineerData && (
                        <>
                          {(name.startsWith('data') || name.startsWith('bulk-merge')) && (
                            <>
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/supplier-map' }}
                                textProps={{ children: 'Supplier Map', className: 'pl-1' }}
                                active={name === 'data/supplier-map'}
                                dataCy="data-supplier-map-sidebar"
                                key="data-supplier-map-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/customer-map' }}
                                textProps={{ children: 'Customer Map', className: 'pl-1' }}
                                active={name.includes('data/customer-map')}
                                dataCy="data-customer-map-sidebar"
                                key="data-customer-map-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/tsd-map' }}
                                textProps={{ children: 'TSD Map', className: 'pl-1' }}
                                active={name === 'data/tsd-map'}
                                dataCy="data-tsd-map-sidebar"
                                key="data-tsd-map-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/ap-map' }}
                                textProps={{ children: 'AP Map', className: 'pl-1' }}
                                active={name === 'data/ap-map'}
                                dataCy="data-ap-map-sidebar"
                                key="data-ap-map-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/product-map' }}
                                textProps={{ children: 'Product Map', className: 'pl-1' }}
                                active={name === 'data/product-map'}
                                dataCy="data-product-map-sidebar"
                                key="data-product-map-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/rpm-sync' }}
                                textProps={{ children: 'RPM Sync', className: 'pl-1' }}
                                active={name === 'data/rpm-sync'}
                                dataCy="data-rpm-sync-sidebar"
                                key="data-rpm-sync-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/rpm-cleanup' }}
                                textProps={{ children: 'RPM Cleanup', className: 'pl-1' }}
                                active={name === 'data/rpm-cleanup' || name === 'bulk-merge'}
                                dataCy="data-rpm-cleanup-sidebar"
                                key="data-rpm-cleanup-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/create-provisional-asset' }}
                                textProps={{ children: 'New Prov. Assets', className: 'pl-1' }}
                                active={name === 'data/create-provisional-asset'}
                                dataCy="data-create-provisional-asset-sidebar"
                                key="data-create-provisional-asset-sidebar"
                              />
                              <SideBarItem
                                className="h-12"
                                navLinkProps={{ to: '/data/payment-frequency-exceptions' }}
                                textProps={{ children: 'Payment Frequency', className: 'pl-1' }}
                                active={name === 'data/payment-frequency-exceptions'}
                                dataCy="data-payment-frequency-exceptions-sidebar"
                                key="data-payment-frequency-exceptions-sidebar"
                              />
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div></div>
      )}
    </>
  );
};
